'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { Price } from '@7f-ui/components/price';
import { formatDateWithYearShort, useDateLabel } from '@7f-ui/hooks/use-date-label';
import { addedToCartItemsAtom, buildExperienceCartItem } from '@feat/feat-cart/added-to-cart';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useCartItem } from '@feat/feat-cart/item';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { formatDate } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { useSetAtom } from 'jotai';
import Link from 'next/link';
import { PreviewBadge } from 'src/app/_page-chrome/preview-badge';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { useAppExperiencePersonsLabel } from '../_hooks/use-app-experience-persons-label';
import type { EdpTimeslot } from '../_lib/experience-booking-utils';
import { AppBookingQueryError, type BookingQueryError } from './app-booking-query-error';
import { AppAnimatedExperienceCancelation } from './app-experience-cancelation';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppStickyFooter } from './app-sticky-footer';
import { useEdpDateSelectionArm } from './edp-date-selection-arm';
type AppExperienceBookingBoxMobileProps = {
  basePrice: {
    price: number;
    category: string;
  } | null;
  selectedTimeslot: EdpTimeslot | null;
  hasTimeslots: boolean;
  hasAvailableDates: boolean;
  bookingError?: BookingQueryError;
  bookingPending?: boolean;
  onDateClick: () => void;
  onCtaClick: () => void;
};
export function AppExperienceBookingBoxMobile(t0) {
  const $ = _c(56);
  const {
    basePrice,
    selectedTimeslot: edpSelectedTimeslot,
    hasTimeslots,
    hasAvailableDates,
    bookingError,
    bookingPending,
    onDateClick,
    onCtaClick
  } = t0;
  const {
    isVariant
  } = useEdpDateSelectionArm();
  const {
    experience,
    filter
  } = useExperienceFilterContext();
  const {
    allOptions
  } = useExperienceOptions(experience.variants);
  let t1;
  if ($[0] !== allOptions || $[1] !== filter.selectedOptions) {
    t1 = {
      allOptions,
      selectedOptions: filter.selectedOptions,
      fallback: ""
    };
    $[0] = allOptions;
    $[1] = filter.selectedOptions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const paxLabel = useAppExperiencePersonsLabel(t1);
  const setAddedToCartItems = useSetAtom(addedToCartItemsAtom);
  const {
    addExperience
  } = useCartItem();
  let t2;
  if ($[3] !== filter.from || $[4] !== filter.to) {
    t2 = {
      fromDate: filter.from,
      toDate: filter.to,
      formatFromOptions: formatDateWithYearShort,
      formatToOptions: formatDateWithYearShort
    };
    $[3] = filter.from;
    $[4] = filter.to;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const dateLabel = useDateLabel(t2);
  const showError = !bookingError && !bookingPending && !hasAvailableDates && hasTimeslots;
  const hasVariantSelection = isVariant && !!edpSelectedTimeslot;
  const showTimeslotFooter = !bookingError && !bookingPending && hasVariantSelection;
  const isCurrencyMismatch = useIsCartCurrencyMismatch(experience.currency);
  let t3;
  if ($[6] !== addExperience || $[7] !== bookingError || $[8] !== bookingPending || $[9] !== edpSelectedTimeslot || $[10] !== experience || $[11] !== filter.selectedOptions || $[12] !== isCurrencyMismatch || $[13] !== setAddedToCartItems) {
    t3 = () => {
      if (bookingError || bookingPending || isCurrencyMismatch || !edpSelectedTimeslot || !filter.selectedOptions) {
        return;
      }
      addExperience({
        timeslotId: edpSelectedTimeslot.id,
        experienceId: experience.id,
        selectedOptions: filter.selectedOptions
      }, {
        onSuccess: () => {
          setAddedToCartItems([buildExperienceCartItem(experience, {
            startDate: edpSelectedTimeslot.date,
            startTimezone: edpSelectedTimeslot.startTimezone
          })]);
        }
      });
    };
    $[6] = addExperience;
    $[7] = bookingError;
    $[8] = bookingPending;
    $[9] = edpSelectedTimeslot;
    $[10] = experience;
    $[11] = filter.selectedOptions;
    $[12] = isCurrencyMismatch;
    $[13] = setAddedToCartItems;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const handleAddToCart = t3;
  let t4;
  if ($[15] !== bookingError || $[16] !== bookingPending || $[17] !== experience.publicId || $[18] !== handleAddToCart || $[19] !== hasAvailableDates || $[20] !== hasTimeslots || $[21] !== isCurrencyMismatch || $[22] !== onCtaClick || $[23] !== showTimeslotFooter) {
    t4 = () => {
      if (showTimeslotFooter) {
        return {
          children: "In den Warenkorb",
          onClick: handleAddToCart,
          "data-testid": shopTestId("ExperienceBookingBox.cta"),
          href: "",
          disabled: isCurrencyMismatch || !!bookingError || bookingPending
        };
      }
      if (hasTimeslots) {
        return {
          children: "Verf\xFCgbarkeit pr\xFCfen",
          onClick: () => {
            trackClickCta("check availability", {
              type: "experience"
            });
            onCtaClick();
          },
          "data-testid": shopTestId("ExperienceBookingBox.cta"),
          href: "",
          disabled: !hasAvailableDates || isCurrencyMismatch || !!bookingError || bookingPending
        };
      }
      return {
        children: "Termin anfragen",
        as: Link,
        href: `/service/anfrageformular-erlebnisse/?experienceId=${experience.publicId}&subject=APPOINTMENT_REQUEST`,
        "data-testid": shopTestId("ExperienceBookingBox.cta")
      };
    };
    $[15] = bookingError;
    $[16] = bookingPending;
    $[17] = experience.publicId;
    $[18] = handleAddToCart;
    $[19] = hasAvailableDates;
    $[20] = hasTimeslots;
    $[21] = isCurrencyMismatch;
    $[22] = onCtaClick;
    $[23] = showTimeslotFooter;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  const getButtonProps = t4;
  let t5;
  if ($[25] !== basePrice || $[26] !== bookingError || $[27] !== dateLabel || $[28] !== edpSelectedTimeslot || $[29] !== experience.currency || $[30] !== experience.hasMultiplePrices || $[31] !== onDateClick || $[32] !== paxLabel || $[33] !== showError || $[34] !== showTimeslotFooter) {
    t5 = () => {
      if (bookingError) {
        return <><AppBookingQueryError error={bookingError} /><BaseButton className={cx("font-body-small underline underline-offset-2", bookingError.availabilityFailed ? "font-semibold text-error" : "text-primary")} onClick={onDateClick} data-testid={shopTestId("ExperienceBookingBox.date")}>{dateLabel}</BaseButton></>;
      }
      if (showTimeslotFooter) {
        return <div className="flex flex-col"><span className="font-body-small">{formatDate(edpSelectedTimeslot.date)} · {edpSelectedTimeslot.startTime} Uhr</span><span className="font-body-small">{paxLabel}</span><Price amount={edpSelectedTimeslot.price} currency={experience.currency} className="font-body-copy font-semibold" /></div>;
      }
      return <>{!!basePrice && !showError && <><div className="font-body-copy font-semibold">{experience.hasMultiplePrices ? "ab " : null}<Price amount={basePrice.price} currency={experience.currency} /></div><div>{`pro ${basePrice.category}`}</div></>}{showError && <><div className="font-base-copy text-primary">Keine freien Termine.<br />Bitte Zeitraum ändern.</div><BaseButton className="font-body-small font-semibold text-error underline underline-offset-2" onClick={onDateClick}>{dateLabel}</BaseButton></>}</>;
    };
    $[25] = basePrice;
    $[26] = bookingError;
    $[27] = dateLabel;
    $[28] = edpSelectedTimeslot;
    $[29] = experience.currency;
    $[30] = experience.hasMultiplePrices;
    $[31] = onDateClick;
    $[32] = paxLabel;
    $[33] = showError;
    $[34] = showTimeslotFooter;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  const getBookingContent = t5;
  let t6;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PreviewBadge containerClassName="pointer-events-none absolute inset-x-0 bottom-full flex justify-center pb-6" />;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== getBookingContent) {
    t7 = <>{t6}{getBookingContent()}</>;
    $[37] = getBookingContent;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  const body = t7;
  if (hasTimeslots) {
    let t8;
    if ($[39] !== experience.cancelationDeadline || $[40] !== hasVariantSelection) {
      t8 = hasVariantSelection ? undefined : <AppAnimatedExperienceCancelation cancelationHours={experience.cancelationDeadline} />;
      $[39] = experience.cancelationDeadline;
      $[40] = hasVariantSelection;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    let t9;
    if ($[42] !== getButtonProps) {
      t9 = getButtonProps();
      $[42] = getButtonProps;
      $[43] = t9;
    } else {
      t9 = $[43];
    }
    let t10;
    if ($[44] !== body || $[45] !== t8 || $[46] !== t9) {
      t10 = <AppStickyFooter popup={t8} buttonProps={t9}>{body}</AppStickyFooter>;
      $[44] = body;
      $[45] = t8;
      $[46] = t9;
      $[47] = t10;
    } else {
      t10 = $[47];
    }
    return t10;
  }
  let t8;
  if ($[48] !== experience.cancelationDeadline) {
    t8 = <AppAnimatedExperienceCancelation cancelationHours={experience.cancelationDeadline} />;
    $[48] = experience.cancelationDeadline;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] !== getButtonProps) {
    t9 = getButtonProps();
    $[50] = getButtonProps;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  let t10;
  if ($[52] !== body || $[53] !== t8 || $[54] !== t9) {
    t10 = <AppStickyFooter popup={t8} buttonProps={t9}>{body}</AppStickyFooter>;
    $[52] = body;
    $[53] = t8;
    $[54] = t9;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  return t10;
}